import { Button, Collapsible, HStack, Text } from "@chakra-ui/react"
import type { BlocksChanges } from "@pathwright/blocks-engine"
import When from "@pathwright/ui/src/components/when/When"
import { useTranslate } from "@pathwright/ux/src/components/lng/useTranslate"
import Pathicon from "@pathwright/ux/src/components/pathicon/Pathicon"
import { useState } from "react"
import { PublishCardTextButton } from "./ContentPublishCard"
import {
  ContentPublishDetails,
  type OnClickBlockChange
} from "./ContentPublishDiff"

const ContentPublishChanges = ({
  changes,
  needsPublish,
  onDiscard,
  onClickBlockChange
}: {
  changes?: BlocksChanges
  needsPublish: boolean
  onDiscard?: () => void
  onClickBlockChange?: OnClickBlockChange
}) => {
  const { tc } = useTranslate()
  const [expanded, setExpanded] = useState(false)
  const count = needsPublish ? changes?.blocks?.length || 0 : 0
  return (
    <Collapsible.Root
      open={expanded}
      onOpenChange={({ open }) => setExpanded(open)}
      lazyMount
      unmountOnExit
      minW={0}
      css={{ contain: "inline-size" }}
    >
      <HStack justify="space-between" align="start" gap={2}>
        <Collapsible.Trigger asChild>
          <Button
            variant="plain"
            bg="transparent"
            border="none"
            color="inherit"
            h="auto"
            p={0}
            gap={1}
            fontSize="sm"
            fontWeight="700"
            justifyContent="start"
            whiteSpace="normal"
            textAlign="left"
          >
            <Collapsible.Indicator
              display="inline-flex"
              transition="transform 0.2s"
              _open={{ transform: "rotate(90deg)" }}
              _motionReduce={{ transition: "none" }}
            >
              <Pathicon icon="chevron-right" size="12px" />
            </Collapsible.Indicator>
            {needsPublish
              ? tc("{{count}} Block update", { count })
              : tc("Up to date")}
          </Button>
        </Collapsible.Trigger>
        {expanded &&
          needsPublish &&
          !!changes?.lastPublishedDateTime &&
          onDiscard && (
            <PublishCardTextButton fontSize="xs" gap={1} onClick={onDiscard}>
              <Pathicon icon="x-circle" size="12px" />
              {tc("Discard")}
            </PublishCardTextButton>
          )}
      </HStack>
      <Collapsible.Content _motionReduce={{ animation: "none" }}>
        <ContentPublishDetails
          changes={changes}
          limit={5}
          onClickBlockChange={onClickBlockChange}
        />
      </Collapsible.Content>
      {!expanded && (
        <Text
          pl={4}
          fontSize="xs"
          color="gray.500"
          css={{ "& .When, & .TooltipWrapper": { display: "inline" } }}
        >
          {needsPublish && changes?.lastModifiedDateTime ? (
            <>
              {tc("Last updated")}{" "}
              <When date={new Date(changes.lastModifiedDateTime)} fitContent />
            </>
          ) : !needsPublish ? (
            tc("There are no unpublished updates.")
          ) : null}
        </Text>
      )}
    </Collapsible.Root>
  )
}

export default ContentPublishChanges
